import { useEffect, useState } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { adminOrderService } from '../../services'

const DEBOUNCE_MS = 300

/**
 * Optional purchase order link for admin deposits (upload or tag after submit).
 */
export default function AdminDepositLinkOrderField({
  organizationId,
  value,
  onChange,
  disabled = false,
  showSuggestions = true,
}) {
  const [filter, setFilter] = useState('')
  const [taggableOrders, setTaggableOrders] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!organizationId || !showSuggestions) {
      setTaggableOrders([])
      return undefined
    }
    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        setLoading(true)
        const search = (filter || value || '').trim()
        const res = await adminOrderService.listAdminDepositTaggableOrders(
          organizationId,
          search ? { search } : {},
        )
        if (!cancelled) setTaggableOrders(res?.items || [])
      } catch {
        if (!cancelled) setTaggableOrders([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [organizationId, filter, value, showSuggestions])

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      <TextField
        size="small"
        fullWidth
        disabled={disabled}
        label="Link to order ID (optional)"
        placeholder="Enter purchase order number"
        value={value || ''}
        onChange={(e) => onChange?.(e.target.value.replace(/\D/g, ''))}
        helperText="Order must belong to this shop and be Accepted. Type an ID even if it does not appear in suggestions."
        inputProps={{ inputMode: 'numeric' }}
      />
      {showSuggestions ? (
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Search eligible orders
            </Typography>
            {loading ? <CircularProgress size={12} /> : null}
          </Stack>
          <TextField
            size="small"
            fullWidth
            disabled={disabled}
            placeholder="Filter by order number"
            value={filter}
            onChange={(e) => setFilter(e.target.value.replace(/\D/g, ''))}
            sx={{ mb: 1 }}
          />
          {taggableOrders.length > 0 ? (
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {taggableOrders.slice(0, 15).map((o) => (
                <Chip
                  key={o.order_id}
                  size="small"
                  label={`Order #${o.order_id}`}
                  variant={String(value) === String(o.order_id) ? 'filled' : 'outlined'}
                  onClick={() => onChange?.(String(o.order_id))}
                  disabled={disabled}
                />
              ))}
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No matching Accepted orders with open payment — enter an order ID manually if you
              have one.
            </Typography>
          )}
        </Box>
      ) : null}
    </Stack>
  )
}
